import { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, FileText, Trash2, Download, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { usePinPrompt } from "@/components/app/PinPrompt";

/**
 * Reusable file uploader / gallery.
 * props:
 *   kind: "logo" | "signature" | "product" | "receipt" | "attachment"
 *   linked_type / linked_id (optional) — attach to a txn/party/item
 *   multiple (bool) — allow multiple uploads; if false, replaces existing
 *   accept — file input accept string (defaults to images + pdf)
 *   maxItems (int) — soft cap for how many to display
 */
export default function FileUploader({
  kind = "attachment",
  linked_type = null,
  linked_id = null,
  multiple = true,
  accept = "image/png,image/jpeg,image/webp,image/gif,application/pdf",
  maxItems = 50,
  label = "Files",
}) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const pinPrompt = usePinPrompt();

  const load = async () => {
    const q = new URLSearchParams({ kind });
    if (linked_type) q.set("linked_type", linked_type);
    if (linked_id) q.set("linked_id", linked_id);
    try {
      const { data } = await api.get(`/files?${q}`);
      setFiles(data);
    } catch (e) {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, linked_id]);

  const doUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    await pinPrompt();
    setBusy(true);
    try {
      for (const f of fileList) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("kind", kind);
        if (linked_type) fd.append("linked_type", linked_type);
        if (linked_id) fd.append("linked_id", linked_id);
        await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Uploaded");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this file?")) return;
    await pinPrompt();
    try { await api.delete(`/files/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const token = localStorage.getItem("token");
  const previewSrc = (f) => `${API}/files/${f.file_id}/download?auth=${encodeURIComponent(token)}`;

  return (
    <div className="border border-border rounded-md p-4 bg-white" data-testid={`uploader-${kind}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="overline">{label}</div>
        <div className="flex gap-2">
          <input ref={inputRef} type="file" multiple={multiple} accept={accept} className="hidden"
            onChange={(e) => doUpload(e.target.files)} data-testid={`uploader-input-${kind}`} />
          <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`uploader-btn-${kind}`}>
            <Upload size={14} /> {busy ? "Uploading..." : "Upload"}
          </Button>
        </div>
      </div>
      {files.length === 0 && (
        <div className="text-sm text-muted-foreground text-center py-6 border-2 border-dashed border-border rounded-md">
          No files yet. Click Upload to add.
        </div>
      )}
      {files.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {files.slice(0, maxItems).map((f) => {
            const isImg = f.content_type?.startsWith("image/");
            return (
              <div key={f.file_id} className="border border-border rounded-md p-2 group relative" data-testid={`file-card-${f.file_id}`}>
                <a href={previewSrc(f)} target="_blank" rel="noopener noreferrer" className="block">
                  {isImg ? (
                    <img src={previewSrc(f)} alt={f.original_filename}
                      className="w-full h-24 object-cover rounded bg-slate-100" />
                  ) : (
                    <div className="w-full h-24 flex items-center justify-center bg-slate-50 rounded">
                      <FileText size={28} className="text-slate-400" />
                    </div>
                  )}
                </a>
                <div className="mt-1 text-xs truncate" title={f.original_filename}>{f.original_filename}</div>
                <div className="text-[10px] text-muted-foreground">{Math.round((f.size || 0) / 1024)} KB</div>
                <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <a href={previewSrc(f)} download target="_blank" rel="noopener noreferrer"
                    className="p-1 bg-white border border-border rounded shadow hover:bg-slate-50">
                    <Download size={12} />
                  </a>
                  <button onClick={() => del(f.file_id)}
                    className="p-1 bg-white border border-border rounded shadow hover:bg-red-50 text-red-600" data-testid={`file-del-${f.file_id}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
